import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getCerAttiviGestione, createCerAttivo, updateCerAttivo, getCategorieForm,
  type CerAttivo, type CategoriaForm, type Tipologia, type Classificazione,
} from '../../api/settings';
import SettingsCrudSection, { type FieldDef, type FieldOption } from './SettingsCrudSection';
import { parseCerInUso } from '../../lib/cerLedger';

export default function CerIngressoSettings() {
  const { t } = useTranslation();
  const TIPOLOGIE: FieldOption[] = [{ value: 'urbano', label: t('settings.cer_ingresso.tip_urbano') }, { value: 'speciale', label: t('settings.cer_ingresso.tip_speciale') }];
  const CLASSIFICAZIONI: FieldOption[] = [{ value: 'non_pericoloso', label: t('settings.cer_ingresso.cls_non_pericoloso') }, { value: 'pericoloso', label: t('settings.cer_ingresso.cls_pericoloso') }];
  const [items, setItems] = useState<CerAttivo[]>([]);
  const [categorie, setCategorie] = useState<CategoriaForm[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    void (async () => {
      setLoading(true);
      try { setItems(await getCerAttiviGestione()); } finally { setLoading(false); }
    })();
  }, []);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { void (async () => { setCategorie(await getCategorieForm(false)); })(); }, []);

  const categoriaOptions: FieldOption[] = categorie.map((c) => ({ value: c.codice, label: c.nome_visualizzato }));
  const fields: FieldDef[] = [
    { key: 'cer', label: t('settings.cer_fields.cer'), kind: 'cer', required: true, placeholder: t('settings.cer_ingresso.cer_placeholder') },
    { key: 'nome_descrittivo', label: t('settings.cer_fields.descrizione'), kind: 'text', required: true },
    { key: 'categoria_form', label: t('settings.cer_fields.categoria'), kind: 'select', options: categoriaOptions, col: 'w-24',
      display: (v) => categorie.find((c) => c.codice === v)?.nome_visualizzato ?? v ?? '—' },
    { key: 'tipologia', label: t('settings.cer_fields.tipologia'), kind: 'select', required: true, options: TIPOLOGIE, col: 'w-24',
      display: (v) => (v ? v.charAt(0).toUpperCase() + v.slice(1) : '—') },
    { key: 'classificazione', label: t('settings.cer_fields.classificazione'), kind: 'select', required: true, options: CLASSIFICAZIONI, col: 'w-32',
      display: (v) => (v ? v.replace('_', ' ') : '—') },
  ];

  return (
    <SettingsCrudSection<CerAttivo>
      description={t('settings.cer_ingresso.descrizione')}
      fields={fields}
      rows={items}
      loading={loading}
      reload={reload}
      testidPrefix="cer-ing"
      entityLabel={t('settings.cer_ingresso.entita')}
      emptyMsg={t('settings.cer_comune.nessun_cer')}
      isIncomplete={(it) => !!it.attivo && (!it.tipologia || !it.classificazione)}
      onCreate={async (v) => {
        await createCerAttivo({
          cer: v.cer.trim(), nome_descrittivo: v.nome_descrittivo.trim(),
          categoria_form: v.categoria_form || null,
          tipologia: v.tipologia as Tipologia, classificazione: v.classificazione as Classificazione,
        });
      }}
      onUpdate={async (id, v) => {
        try {
          await updateCerAttivo(id, {
            cer: v.cer.trim(),
            nome_descrittivo: v.nome_descrittivo.trim(), categoria_form: v.categoria_form || null,
            tipologia: v.tipologia as Tipologia, classificazione: v.classificazione as Classificazione,
          });
        } catch (err: unknown) {
          // #116 — cambiare il CODICE con kg già a saldo propagherebbe su N
          // ordini in un colpo: il backend rifiuta con un 409 strutturato.
          // Il detail è un OGGETTO, che apiErrorMessage non sa rendere (cade
          // sul fallback generico): lo si traduce qui in un detail testuale
          // localizzato, nella forma che apiErrorMessage legge davvero.
          const inUso = parseCerInUso(err);
          if (inUso) {
            throw { response: { data: { detail: t('settings.cer_ingresso.errore_cer_in_uso', {
              ordini: inUso.ordini, conferimenti: inUso.conferimenti,
            }) } } };
          }
          throw err;
        }
      }}
      onToggle={async (it) => {
        if (!it.id) return;
        if (!it.attivo && (!it.tipologia || !it.classificazione)) {
          // Errore LOCALE, non venuto dalla rete: prende la forma di una
          // risposta API perché il gestore comune (SettingsCrudSection →
          // apiErrorMessage) legge di lì. Nessun marcatore axios richiesto.
          throw { response: { data: { detail: t('settings.cer_ingresso.errore_riattiva', { cer: it.cer }) } } };
        }
        await updateCerAttivo(it.id, { attivo: !it.attivo });
      }}
    />
  );
}
